import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Trophy } from 'lucide-react';
import { cn } from '../lib/utils';
import { CHAPTER_ZERO_OVERLAY_Z_INDEX } from './ChapterZeroAchievementToast';
import { ScribaneMark } from './ScribaneMark';

type OnboardingTrophyRingProps = {
  points: number;
  targetPoints: number;
  size?: number;
  active?: boolean;
  celebrationKey?: number;
  className?: string;
};

const VIEWBOX = 48;
const STROKE_WIDTH = 3;
const CENTER = VIEWBOX / 2;
const RADIUS = (VIEWBOX - STROKE_WIDTH * 2) / 2;
const CIRCUMFERENCE = 2 * Math.PI * RADIUS;
const SPIN_MS = 700;
const MORPH_MS = 280;
const INTRO_GLOW_MS = 2200;
const CELEBRATE_GLOW_MS = 2400;
const CELEBRATE_SPIN_MS = 900;
const INTRO_GLOW_STORAGE_KEY = 'chapter-zero-intro-glow';

type IntroGlowSession = {
  pageLoadId: string;
  until: number;
};

function getPageLoadId(): string {
  return String(performance.timeOrigin);
}

function readIntroGlowSession(): IntroGlowSession | null {
  if (typeof sessionStorage === 'undefined') {
    return null;
  }
  const raw = sessionStorage.getItem(INTRO_GLOW_STORAGE_KEY);
  if (!raw) {
    return null;
  }
  try {
    return JSON.parse(raw) as IntroGlowSession;
  } catch {
    return null;
  }
}

function writeIntroGlowSession(until: number): void {
  if (typeof sessionStorage === 'undefined') {
    return;
  }
  sessionStorage.setItem(
    INTRO_GLOW_STORAGE_KEY,
    JSON.stringify({ pageLoadId: getPageLoadId(), until })
  );
}

function shouldShowIntroGlow(): boolean {
  const pageLoadId = getPageLoadId();
  const stored = readIntroGlowSession();
  if (!stored || stored.pageLoadId !== pageLoadId) {
    return true;
  }
  return Date.now() < stored.until;
}

export const OnboardingTrophyRing: React.FC<OnboardingTrophyRingProps> = ({
  points,
  targetPoints,
  size = 40,
  active = false,
  celebrationKey = 0,
  className,
}) => {
  const ratio = targetPoints > 0 ? Math.min(Math.max(points / targetPoints, 0), 1) : 0;
  const dashOffset = CIRCUMFERENCE * (1 - ratio);
  const ringRef = useRef<HTMLSpanElement>(null);
  const [spinning, setSpinning] = useState(false);
  const [showMark, setShowMark] = useState(false);
  const [introGlow, setIntroGlow] = useState(shouldShowIntroGlow);
  const [celebrateGlow, setCelebrateGlow] = useState(false);
  const [glowRect, setGlowRect] = useState<DOMRect | null>(null);

  useEffect(() => {
    if (!introGlow) {
      return;
    }

    writeIntroGlowSession(Date.now() + INTRO_GLOW_MS);
    const glowTimer = window.setTimeout(() => setIntroGlow(false), INTRO_GLOW_MS);
    return () => window.clearTimeout(glowTimer);
  }, [introGlow]);

  useEffect(() => {
    if (!active) {
      setSpinning(false);
      setShowMark(false);
      return;
    }

    setSpinning(true);
    setShowMark(false);
    const morphTimer = window.setTimeout(() => setShowMark(true), MORPH_MS);
    const settleTimer = window.setTimeout(() => setSpinning(false), SPIN_MS);

    return () => {
      window.clearTimeout(morphTimer);
      window.clearTimeout(settleTimer);
    };
  }, [active]);

  useEffect(() => {
    if (celebrationKey <= 0) {
      return;
    }

    setCelebrateGlow(true);
    setSpinning(true);
    setShowMark(false);
    const morphTimer = window.setTimeout(() => setShowMark(true), MORPH_MS);
    const spinTimer = window.setTimeout(() => setSpinning(false), CELEBRATE_SPIN_MS);
    const glowTimer = window.setTimeout(() => setCelebrateGlow(false), CELEBRATE_GLOW_MS);

    return () => {
      window.clearTimeout(morphTimer);
      window.clearTimeout(spinTimer);
      window.clearTimeout(glowTimer);
    };
  }, [celebrationKey]);

  const glowing = introGlow || celebrateGlow;
  const filled = active || celebrateGlow;

  useLayoutEffect(() => {
    if (!glowing) {
      setGlowRect(null);
      return;
    }

    const updateRect = () => {
      setGlowRect(ringRef.current?.getBoundingClientRect() ?? null);
    };

    updateRect();
    window.addEventListener('resize', updateRect);
    window.addEventListener('scroll', updateRect, true);
    return () => {
      window.removeEventListener('resize', updateRect);
      window.removeEventListener('scroll', updateRect, true);
    };
  }, [glowing, size]);

  return (
    <>
      <style>{`
        @keyframes chapter-zero-spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
        .chapter-zero-spinning {
          animation: chapter-zero-spin ${SPIN_MS}ms ease-in-out forwards;
        }
        .chapter-zero-celebrate-spinning {
          animation: chapter-zero-spin ${CELEBRATE_SPIN_MS}ms ease-in-out forwards;
        }
        @keyframes chapter-zero-intro-glow {
          0%, 100% {
            box-shadow:
              0 0 0 0 color-mix(in srgb, var(--scribane-green) 0%, transparent),
              0 0 0 0 color-mix(in srgb, var(--scribane-green) 0%, transparent);
          }
          20%, 55% {
            box-shadow:
              0 0 10px 3px color-mix(in srgb, var(--scribane-green) 75%, transparent),
              0 0 22px 8px color-mix(in srgb, var(--scribane-green) 40%, transparent);
          }
        }
        .chapter-zero-intro-glow {
          animation: chapter-zero-intro-glow ${INTRO_GLOW_MS}ms ease-in-out forwards;
        }
        @keyframes chapter-zero-celebrate-glow {
          0%, 100% {
            box-shadow:
              0 0 0 0 color-mix(in srgb, var(--scribane-green) 0%, transparent),
              0 0 0 0 color-mix(in srgb, var(--scribane-green) 0%, transparent);
          }
          15%, 50% {
            box-shadow:
              0 0 14px 5px color-mix(in srgb, var(--scribane-green) 90%, transparent),
              0 0 34px 14px color-mix(in srgb, var(--scribane-green) 55%, transparent);
          }
          30% {
            box-shadow:
              0 0 18px 7px color-mix(in srgb, var(--scribane-green) 95%, transparent),
              0 0 42px 18px color-mix(in srgb, var(--scribane-green) 65%, transparent);
          }
        }
        .chapter-zero-celebrate-glow {
          animation: chapter-zero-celebrate-glow ${CELEBRATE_GLOW_MS}ms ease-in-out forwards;
        }
      `}</style>
      <span
        ref={ringRef}
        className={cn(
          'relative inline-flex shrink-0 items-center justify-center overflow-visible rounded-full',
          className
        )}
        style={{ width: size, height: size }}
      >
        <svg
          viewBox={`0 0 ${VIEWBOX} ${VIEWBOX}`}
          width={size}
          height={size}
          className="absolute inset-0 size-full -rotate-90 overflow-visible"
          aria-hidden="true"
          focusable="false"
        >
          <circle
            cx={CENTER}
            cy={CENTER}
            r={RADIUS}
            fill="none"
            stroke="currentColor"
            strokeWidth={STROKE_WIDTH}
            strokeOpacity={glowing ? (celebrateGlow ? 0.85 : 0.55) : 0.22}
            className="text-[var(--scribane-green)] transition-[stroke-opacity] duration-500"
          />
          {ratio > 0 && (
            <circle
              cx={CENTER}
              cy={CENTER}
              r={RADIUS}
              fill="none"
              stroke="currentColor"
              strokeWidth={STROKE_WIDTH}
              strokeLinecap="round"
              strokeDasharray={CIRCUMFERENCE}
              strokeDashoffset={dashOffset}
              className="text-[var(--scribane-green)] transition-[stroke-dashoffset] duration-500 ease-out"
            />
          )}
        </svg>

        <span
          className={cn(
            'absolute z-[1] flex items-center justify-center rounded-full transition-colors duration-300',
            filled ? 'bg-[var(--scribane-green)]' : 'bg-white dark:bg-background'
          )}
          style={{
            width: size * 0.72,
            height: size * 0.72,
          }}
        >
          <span
            className={cn(
              'relative flex size-[92%] items-center justify-center',
              spinning && (celebrateGlow ? 'chapter-zero-celebrate-spinning' : 'chapter-zero-spinning')
            )}
          >
            <Trophy
              absoluteStrokeWidth
              className={cn(
                'absolute size-[85%] transition-opacity duration-150',
                showMark ? 'opacity-0' : 'opacity-100',
                filled ? 'text-[var(--scribane-black)]' : 'text-foreground'
              )}
              strokeWidth={2}
              aria-hidden="true"
            />
            <ScribaneMark
              className={cn(
                'absolute size-[120%] transition-opacity duration-150',
                showMark ? 'opacity-100' : 'opacity-0',
                'text-[var(--scribane-black)]'
              )}
            />
          </span>
        </span>
      </span>
      {glowing &&
        glowRect &&
        createPortal(
          <span
            aria-hidden="true"
            className={cn(
              'pointer-events-none block rounded-full',
              introGlow && !celebrateGlow && 'chapter-zero-intro-glow',
              celebrateGlow && 'chapter-zero-celebrate-glow'
            )}
            style={{
              position: 'fixed',
              left: glowRect.left,
              top: glowRect.top,
              width: glowRect.width,
              height: glowRect.height,
              zIndex: CHAPTER_ZERO_OVERLAY_Z_INDEX - 1,
            }}
          />,
          document.body
        )}
    </>
  );
};
